import { AppContext } from 'context/AppContext';
import { useState, useRef, useCallback, useContext } from 'react';

export const usePreciseDragDropGant = ({
  chartRef,
  minDate,
  maxDate,
  totalChartWidth,
  isSnapping = false,
  snapMinutes = 60,
  useGrabOffset = false,
  onDropFreeSpace,
  onDropOnStage,
}) => {
  const [draggedStage, setDraggedStage] = useState(null);
  
  // Zamiast trzymać wszystkie dane podglądu, trzymamy tylko flagę czy go pokazać
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const { procesory } = useContext(AppContext);
  
  const grabOffsetPxRef = useRef(0);

  // Referencje do elementów DOM podglądu (przekażemy je do komponentu rodzica)
  const previewRefs = {
    line: useRef(null),
    tooltip: useRef(null),
    time: useRef(null),
    badge: useRef(null)
  };

  const formatDateForApi = (dateObj) => { /* ...Twój kod bez zmian... */
    if (!dateObj) return null;
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatDateForTooltip = (dateObj) => { /* ...Twój kod bez zmian... */
    if (!dateObj) return '';
    const days = ['Nd', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'So'];
    const dayName = days[dateObj.getDay()];
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${dayName} ${day}.${month} | ${hours}:${minutes}`;
  };

  const calculateTimeFromClientX = useCallback(
    (clientX) => { /* ...Twój kod bez zmian... */
      if (!chartRef.current || !minDate || !maxDate || !totalChartWidth) return null;

      const rect = chartRef.current.getBoundingClientRect();
      const rawXPx = clientX - rect.left - (useGrabOffset ? grabOffsetPxRef.current : 0);
      const clampedXPx = Math.max(0, Math.min(rawXPx, totalChartWidth));

      const totalTimeSpanMs = maxDate.getTime() - minDate.getTime();
      const timeOffsetMs = (clampedXPx / totalChartWidth) * totalTimeSpanMs;
      const rawTimestampMs = minDate.getTime() + timeOffsetMs;

      const oneMinuteMs = 60 * 1000;
      let targetDate = new Date(Math.round(rawTimestampMs / oneMinuteMs) * oneMinuteMs);

      if (isSnapping && snapMinutes > 1) {
        const snapMs = snapMinutes * 60 * 1000;
        targetDate = new Date(Math.round(targetDate.getTime() / snapMs) * snapMs);
      }

      const finalOffsetMs = targetDate.getTime() - minDate.getTime();
      const snappedXPx = (finalOffsetMs / totalTimeSpanMs) * totalChartWidth;

      return {
        dateObj: targetDate,
        apiDate: formatDateForApi(targetDate),
        displayDate: formatDateForTooltip(targetDate),
        xPx: snappedXPx,
        cursorXPx: clientX - rect.left,
        cursorYPx: rect.top,
      };
    },
    [chartRef, minDate, maxDate, totalChartWidth, isSnapping, snapMinutes, useGrabOffset]
  );

  // Funkcja do bezpośredniej manipulacji DOM (Omija React render cycle!)
  const updatePreviewDOM = (mode, timeData, relativeY, targetStage, processorId) => {
    // 1. Aktualizacja pozycji na ekranie przez GPU (transform)
    if (previewRefs.line.current) {
      previewRefs.line.current.style.transform = `translate3d(${timeData.xPx}px, 0, 0)`;
      // Zmiana atrybutu data-mode dla ostylowania w CSS
      previewRefs.line.current.setAttribute('data-mode', mode); 
    }

    if (previewRefs.tooltip.current) {
      previewRefs.tooltip.current.style.transform = `translate3d(${timeData.xPx}px, ${Math.max(40, relativeY - 55)}px, 0)`;
    }

    // 2. Aktualizacja tekstów
    if (previewRefs.time.current && previewRefs.time.current.textContent !== timeData.displayDate) {
      previewRefs.time.current.textContent = timeData.displayDate;
    }

    if (previewRefs.badge.current) {
      const targetProcesorObj = procesory.find(x => parseInt(x.id) === parseInt(processorId));
      const procName = targetProcesorObj ? targetProcesorObj.nazwa : '';
      
      const badgeText = mode === 'ON_STAGE' 
        ? `Na klocek: ${targetStage?.nr || ''} ${targetStage?.tytul || 'Przerwa'}` 
        : `Wolne miejsce (${procName})`;

      if (previewRefs.badge.current.textContent !== badgeText) {
        previewRefs.badge.current.textContent = badgeText;
      }
      // Zmiana atrybutu dla CSS
      previewRefs.badge.current.setAttribute('data-mode', mode);
    }
  };

  const handleDragStart = (stage, e) => {
    sessionStorage.setItem("proces_nazwa_id", stage.proces_nazwa_id);
    e.dataTransfer.setData("application/json", JSON.stringify(stage));
    const barRect = e.currentTarget.getBoundingClientRect();
    grabOffsetPxRef.current = e.clientX - barRect.left;
    setDraggedStage(stage);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(stage.global_id));
  };

  const handleDragOverStage = (targetStage, processorId, e) => {
    if (!draggedStage) return;
    // ...Twoje weryfikacje reguł biznesowych... (pozostawione bez zmian)
    const dragProcesId = parseInt(sessionStorage.getItem("proces_nazwa_id") || draggedStage.proces_nazwa_id);
    const targetProcesId = parseInt(targetStage.proces_nazwa_id);
    const dragProcesorId = parseInt(draggedStage.procesor_id);
    const targetProcesorId = parseInt(processorId);
    const dragTypGrupy = parseInt(draggedStage.typ_grupy);

    if (isNaN(dragProcesId) || isNaN(targetProcesId) || dragProcesId != targetProcesId) return; 

    const isChangingProcessor = dragProcesorId != targetProcesorId;
    if (isChangingProcessor) {
      if (dragProcesId == 6) return;
      if (dragTypGrupy != 2) return;
    }

    if (targetStage.global_id === draggedStage.global_id) {
      if (dragTypGrupy === 1) {
        e.stopPropagation(); 
        setIsPreviewVisible(false);
        return; 
      }
      return;
    }

    e.preventDefault(); 
    e.dataTransfer.dropEffect = 'move';
    e.stopPropagation();

    // Aktywujemy widoczność kontenerów podglądu (wywoła się tylko raz!)
    if (!isPreviewVisible) setIsPreviewVisible(true);

    const timeData = calculateTimeFromClientX(e.clientX);
    if (!timeData) return;

    const chartRect = chartRef.current.getBoundingClientRect();
    const relativeY = e.clientY - chartRect.top;

    // Bezpośrednia zmiana DOM
    updatePreviewDOM('ON_STAGE', timeData, relativeY, targetStage, processorId);
  };

  const handleDragOverRow = (processorId, e) => {
    if (!draggedStage) return;
    // ...Twoje weryfikacje reguł biznesowych... (pozostawione bez zmian)
    const targetProcesorObj = procesory.find(x => parseInt(x.id) === parseInt(processorId));
    const targetProcesId = targetProcesorObj ? parseInt(targetProcesorObj.grupa) : NaN;
    const dragProcesId = parseInt(sessionStorage.getItem("proces_nazwa_id") || draggedStage.proces_nazwa_id);
    const dragProcesorId = parseInt(draggedStage.procesor_id);
    const targetProcesorId = parseInt(processorId);
    const dragTypGrupy = parseInt(draggedStage.typ_grupy);

    if (isNaN(dragProcesId) || isNaN(targetProcesId) || dragProcesId !== targetProcesId) return;
    const isChangingProcessor = dragProcesorId !== targetProcesorId;
    if (isChangingProcessor) {
      if (dragProcesId === 6) return;
      if (dragTypGrupy !== 2) return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!isPreviewVisible) setIsPreviewVisible(true);

    const timeData = calculateTimeFromClientX(e.clientX);
    if (!timeData) return;

    const chartRect = chartRef.current.getBoundingClientRect();
    const relativeY = e.clientY - chartRect.top;

    // Bezpośrednia zmiana DOM
    updatePreviewDOM('FREE_SPACE', timeData, relativeY, null, processorId);
  };

  const handleDropOnStage = (targetStage, processorId, e) => {
    e.preventDefault();
    if (!draggedStage) return;
    if (targetStage.global_id === draggedStage.global_id) return;
    e.stopPropagation();

    const timeData = calculateTimeFromClientX(e.clientX);
    if (timeData && onDropOnStage) {
      onDropOnStage({
        draggedStage,
        targetStage,
        targetProcessorId: Number(processorId),
        dropDate: timeData.apiDate,     
        dropDateObj: timeData.dateObj,  
      });
    }
    setDraggedStage(null);
    setIsPreviewVisible(false); // Ukrywamy podgląd
  };

  const handleDropOnRow = (processorId, e) => {
    e.preventDefault();
    if (!draggedStage) return;

    const timeData = calculateTimeFromClientX(e.clientX);
    if (timeData && onDropFreeSpace) {
      onDropFreeSpace({
        draggedStage,
        targetProcessorId: Number(processorId),
        dropDate: timeData.apiDate,     
        dropDateObj: timeData.dateObj,  
      });
    }
    setDraggedStage(null);
    setIsPreviewVisible(false); // Ukrywamy podgląd
  };

  const handleDragEnd = () => {
    setDraggedStage(null);
    setIsPreviewVisible(false); // Ukrywamy podgląd
  };

  return {
    draggedStage,
    isPreviewVisible,
    previewRefs, // Zwracamy referencje do wpięcia w widok!
    handleDragStart,
    handleDragOverStage,
    handleDragOverRow,
    handleDropOnStage,
    handleDropOnRow,
    handleDragEnd,
  };
};